import { useEffect, useState } from 'react';
import {
  Box, Paper, Stepper, Step, StepLabel, Button, Typography,
  Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
  Alert, AlertTitle, Stack, TextField,
} from '@mui/material';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import { enableAccessAnalyzer, getAccessAnalyzerConfig } from '../../../services/accessAnalyzerApi';
import { listSecurityHubAccounts } from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { POLL_INTERVAL_MS } from './pollInterval';
import MultiSelectField from './MultiSelectField';
import OperationProgress from './OperationProgress';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, sectionHeadingSx, actionRowSx, insetPanelSx,
} from '../portalStyles';

// Must match `color` in engines/accessanalyzer.js and AccessAnalyzerEntry.js.
const ACCESS_ANALYZER_COLOR = '#AD1457';
const ACCESS_ANALYZER_COLOR_DARK = '#8C1049'; // one dark per engine, as the sibling wizards do

// NO ADMIN-ACCOUNT STEP, and there must never be one. Prism creates ACCOUNT-scoped
// analyzers standalone in each account through that account's own switch role: the
// ORGANIZATION analyzer types would need organizations:RegisterDelegatedAdministrator
// from the management account, which Prism has no route to. No enrolled account is
// privileged and EnableAccessAnalyzerRequest carries no adminAccountId. There is no
// member-email step either - Access Analyzer has no invitation model.
const steps = ['Accounts', 'Regions', 'Unused access', 'Review'];
const ACCOUNTS_STEP = 0;
const REGIONS_STEP = 1;
const UNUSED_STEP = 2;
const REVIEW_STEP = 3;

// AWS's own valid range for UnusedAccessConfiguration.unusedAccessAge, and the
// console default. The backend normalises an out-of-range value rather than
// rejecting it, but the picker still enforces the range so the number the user
// reads back is the number that will be stored.
const AGE_MIN = 1;
const AGE_MAX = 365;
const AGE_DEFAULT = 90;

// THE PAID SWITCH. Kept as named constants so the wizard and
// AccessAnalyzerSettingsPage.js - the only other place this price is quoted -
// state the same figure; a price that disagrees with itself across two screens is
// a support ticket at best.
const UNUSED_ACCESS_PRICE = '$0.20 per IAM role or user per month, per account';
const UNUSED_ACCESS_PRICE_NOTE = 'Charges start as soon as the analyzer is created and may not be prorated if you turn the feature off mid-month. AWS list price, US regions, as of writing - check the IAM Access Analyzer pricing page for your regions.';

// Access-Analyzer-accented primary button (the theme default is the CSPM blue).
const accessAnalyzerButtonSx = {
  backgroundColor: ACCESS_ANALYZER_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: ACCESS_ANALYZER_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const quietButtonSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  color: CK.body,
  '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.26)' },
};

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };
const captionTextSx = { fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted };

// One row of the Review step - a fixed label column plus the chosen value, so
// every row shares a single baseline grid.
function ReviewRow({ label, value }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{ py: 0.5 }}
    >
      <Typography sx={{ minWidth: 160, fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: CK.navy,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

// The zone-of-trust disclosure, rendered on the Accounts step AND restated on
// Review.
//
// This is the predicted top support ticket for the capability, and it belongs in
// the wizard rather than only in the docs: Prism creates ACCOUNT-scoped analyzers,
// so the zone of trust is a single account and a grant to another account inside
// the customer's own AWS Organization is reported as external access. An
// organization-scoped analyzer - the one a customer would create by hand in the
// console - treats exactly that grant as trusted. The result is strictly noisier
// than the console view, and an operator who has not been told will file it as
// false positives.
function ZoneOfTrustNotice({ sx }) {
  return (
    <Alert severity="info" sx={sx}>
      <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
        Access between your own accounts is reported as external
      </AlertTitle>
      Prism creates an <b>account-scoped</b> analyzer in each account, so the zone of
      trust is that one account. Access granted to <b>another account in your own AWS
      Organization</b> is therefore reported as an external-access finding. An
      organization-scoped analyzer - what you would get by enabling Access Analyzer
      by hand in the console with an organization as the zone of trust - treats that
      same access as trusted and stays silent about it.
      <br /><br />
      This view is deliberately stricter and noisier than the console&apos;s
      organization view. The findings are real cross-account access, not false
      positives; archive the ones you have judged acceptable and they stop
      resurfacing.
    </Alert>
  );
}

export default function AccessAnalyzerSetup({ onComplete, priorFailure }) {
  const [active, setActive] = useState(ACCOUNTS_STEP);
  const [accounts, setAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form state - exactly the five fields of models.EnableAccessAnalyzerRequest.
  const [homeRegion, setHomeRegion] = useState('us-east-1');
  const [linkingRegions, setLinkingRegions] = useState(['us-east-1']);
  const [memberAccountIds, setMemberAccountIds] = useState([]);
  // Defaults to OFF, and must stay that way: this family is billed per IAM
  // principal per month, so a default of true would opt every new tenant into a
  // charge they never asked for.
  const [unusedAccessEnabled, setUnusedAccessEnabled] = useState(false);
  const [unusedAccessAge, setUnusedAccessAge] = useState(String(AGE_DEFAULT));

  // Post-submit state: the config row the POST answered with.
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listSecurityHubAccounts();
        if (cancelled) return;
        setAccounts(res?.data?.accounts || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Poll while the enable fan-out runs. Keyed on the status rather than the whole
  // config so the interval is not rebuilt on every progress tick.
  useEffect(() => {
    if (config?.status !== 'in_progress') return undefined;
    const id = setInterval(async () => {
      try {
        const res = await getAccessAnalyzerConfig();
        if (res?.data) setConfig(res.data);
      } catch (_) {
        // Transient failures are swallowed: the next tick retries, and a dead
        // backend already shows up as a stalled progress bar rather than as a
        // wizard that throws away a run which may well be succeeding.
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [config?.status]);

  // Hand back to AccessAnalyzerEntry the moment the run settles, whatever it
  // settled as. This also covers the (unlikely) race where the fan-out finishes
  // before the POST response is rendered - without it the poll above would never
  // start and the wizard would sit on a terminal card with no way forward.
  //
  // Entry then routes on the settled status: enabled/partial_failure to the
  // dashboard, failed back to this wizard with priorFailure set.
  useEffect(() => {
    if (config && config.status !== 'in_progress' && onComplete) onComplete();
  }, [config, onComplete]);

  const next = () => setActive((s) => s + 1);
  const back = () => setActive((s) => s - 1);

  const ageNumber = Number(unusedAccessAge);
  const ageValid = Number.isInteger(ageNumber) && ageNumber >= AGE_MIN && ageNumber <= AGE_MAX;

  const stepComplete = (step) => {
    if (step === ACCOUNTS_STEP) return memberAccountIds.length > 0;
    // The backend force-prepends the home region to linkingRegions anyway; the
    // membership check exists so the Review step cannot understate the blast
    // radius of what is about to be enabled.
    if (step === REGIONS_STEP) {
      return !!homeRegion && linkingRegions.length > 0 && linkingRegions.includes(homeRegion);
    }
    // The age only has to be valid when the paid family is actually on - the field
    // is disabled otherwise, and a user who left it blank while the switch is off
    // must not be blocked on a value nothing will read.
    if (step === UNUSED_STEP) return !unusedAccessEnabled || ageValid;
    return true;
  };

  const canSubmit = stepComplete(ACCOUNTS_STEP)
    && stepComplete(REGIONS_STEP)
    && stepComplete(UNUSED_STEP);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await enableAccessAnalyzer({
        homeRegion,
        linkingRegions,
        memberAccountIds,
        unusedAccessEnabled,
        // Sent even when the switch is off so toggling the feature on later does
        // not silently adopt a different window than the one reviewed here.
        unusedAccessAge: ageValid ? ageNumber : AGE_DEFAULT,
      });
      // Guard the empty envelope: without it a 2xx carrying no config would leave
      // the wizard sitting on Review with the button re-enabled and nothing said.
      if (!res?.data) throw new Error('IAM Access Analyzer was not enabled - the server returned no configuration.');
      setConfig(res.data);
    } catch (e) {
      // portalFetch rejects with the backend's error string and no status code, so
      // there is nothing to branch on. Every rejection here is a validation failure
      // against one of the inputs ("account … does not belong to this
      // organization", "unsupported region", "already enabled"), and the user can
      // reach all of them with Back - so the message stays on Review rather than
      // guessing which step to jump to.
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const accountLabel = (id) => {
    const acct = accounts.find((a) => a.accountId === id);
    return acct ? `${acct.name || acct.accountId} (${acct.accountId})` : id;
  };

  const body = () => {
    if (config) {
      const statusLabel = {
        in_progress: 'Setting up IAM Access Analyzer…',
        enabled: 'IAM Access Analyzer is enabled',
        partial_failure: 'IAM Access Analyzer enabled with partial failures',
        failed: 'Setup failed',
        disabled: 'IAM Access Analyzer is disabled',
      }[config.status] || config.status;
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>{statusLabel}</Typography>
          {config.status === 'in_progress' && (
            <>
              <Typography sx={{ ...bodyTextSx, mt: 1 }}>
                Granting scoped permissions and creating an analyzer in each account.
                Accounts are enrolled independently, so results appear as each one
                finishes.
              </Typography>
              <OperationProgress
                percent={config.progress}
                label={config.progressLabel || 'Working…'}
                sx={{ my: 2 }}
              />
            </>
          )}
          {config.status === 'partial_failure' && (
            <Typography sx={{ ...bodyTextSx, mt: 1 }}>
              Some accounts could not be enrolled. The rest are already analyzing -
              retry the failed ones, or repair their role permissions, from the
              Enrollment page.
            </Typography>
          )}
          {config.errorMessage && (
            <Alert severity="warning" sx={{ mt: 2 }}>{config.errorMessage}</Alert>
          )}
          {/* Adoption is stated the moment it happens, not only on the Enrollment
              page: an adopted analyzer is a permanent asymmetry - it is enabled
              like any other cell but is deliberately skipped by teardown. */}
          {config.adoptedCount > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {config.adoptedCount} existing {config.adoptedCount === 1 ? 'analyzer was' : 'analyzers were'} adopted
              rather than recreated. Prism reads their findings and will never delete
              them - deleting an analyzer destroys every finding it holds.
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <ReviewRow label="Home region" value={config.homeRegion} />
            <ReviewRow label="Accounts" value={String(config.accountCount ?? memberAccountIds.length)} />
          </Box>
        </Box>
      );
    }

    if (active === ACCOUNTS_STEP) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Pick the accounts to analyze. IAM Access Analyzer runs standalone in each
            one, so there is no administrator account to nominate and no account is
            privileged over another. External-access analysis costs nothing. You can
            add or remove accounts later without tearing anything down.
          </Typography>
          <ZoneOfTrustNotice sx={{ mb: 2 }} />
          <MultiSelectField
            label="Accounts"
            options={accounts.map((a) => ({
              value: a.accountId,
              label: `${a.name || a.accountId} (${a.accountId})`,
            }))}
            value={memberAccountIds}
            onChange={setMemberAccountIds}
          />
          {accounts.length === 0 && (
            <Typography sx={{ ...captionTextSx, mt: 1 }}>
              No linked AWS accounts were returned for this organization.
            </Typography>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
      );
    }

    if (active === REGIONS_STEP) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            IAM Access Analyzer only evaluates policies attached to resources in the
            same region as the analyzer, so external access needs one analyzer per
            region you pick, in every account. The home region aggregates nothing -
            Prism does that itself - but it is always analyzed, it is the region
            Prism talks to by default, and it is the only region that carries the
            unused-access analyzer and the findings for global resources such as IAM
            roles.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Home region</InputLabel>
            <Select
              value={homeRegion}
              label="Home region"
              onChange={(e) => {
                const v = e.target.value;
                setHomeRegion(v);
                if (!linkingRegions.includes(v)) setLinkingRegions([v, ...linkingRegions]);
              }}
            >
              {AWS_REGIONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          <MultiSelectField
            label="Analyzed regions"
            options={AWS_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
            value={linkingRegions}
            onChange={setLinkingRegions}
            lockedValues={homeRegion ? [homeRegion] : []}
          />
          <Typography sx={{ ...captionTextSx, mt: 1 }}>
            External-access analyzers are free in every region. Adding regions
            increases coverage, not cost.
          </Typography>
        </Box>
      );
    }

    if (active === UNUSED_STEP) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Unused access is the second analyzer family. It finds IAM roles, access
            keys, console passwords and permissions that nobody has used inside a
            tracking window - the surface you can safely delete. Unlike
            external-access analysis, <b>AWS bills for it</b>, so it is off by
            default.
          </Typography>
          {/* Not fine print. This is the one switch on this screen that spends the
              customer's money, and the figure has to be next to the switch rather
              than under it. */}
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              Unused access is a paid feature - {UNUSED_ACCESS_PRICE}
            </AlertTitle>
            Prism creates exactly <b>one unused-access analyzer per enrolled account,
            in the home region only</b>. These analyzers are region-independent -
            IAM roles and users are global - so fanning them across your other
            regions would multiply the bill for byte-identical findings and Prism
            deliberately does not do it.
            <br /><br />
            {UNUSED_ACCESS_PRICE_NOTE}
          </Alert>
          <Box sx={insetPanelSx}>
            <FormControlLabel
              control={
                <Switch
                  checked={unusedAccessEnabled}
                  onChange={(e) => setUnusedAccessEnabled(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: ACCESS_ANALYZER_COLOR },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCESS_ANALYZER_COLOR },
                  }}
                />
              }
              label={(
                <Box>
                  <Typography
                    sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', fontWeight: 600, color: CK.navy }}
                  >
                    Analyze unused access
                  </Typography>
                  <Typography sx={bodyTextSx}>
                    Creates one ACCOUNT_UNUSED_ACCESS analyzer per account in{' '}
                    <b>{homeRegion}</b>.
                  </Typography>
                </Box>
              )}
              sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
            />
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Tracking period (days)"
                type="number"
                size="small"
                value={unusedAccessAge}
                disabled={!unusedAccessEnabled}
                onChange={(e) => setUnusedAccessAge(e.target.value)}
                inputProps={{ min: AGE_MIN, max: AGE_MAX, step: 1 }}
                error={unusedAccessEnabled && !ageValid}
                helperText={unusedAccessEnabled && !ageValid
                  ? `Enter a whole number of days between ${AGE_MIN} and ${AGE_MAX}.`
                  : `${AGE_MIN}-${AGE_MAX} days. ${AGE_DEFAULT} is the AWS console default.`}
                sx={{ maxWidth: 260 }}
              />
              <Typography sx={{ ...captionTextSx, mt: 1 }}>
                A role or key counts as unused only if AWS saw no use of it within
                this window. A shorter window finds more; a longer one is safer to
                act on.
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ py: 2 }}>
        <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Review</Typography>
        <ReviewRow
          label="Accounts"
          value={memberAccountIds.length <= 3
            ? memberAccountIds.map(accountLabel).join(', ')
            : `${memberAccountIds.length} accounts`}
        />
        <ReviewRow label="Home region" value={homeRegion} />
        <ReviewRow label="Analyzed regions" value={linkingRegions.join(', ')} />
        <ReviewRow
          label="Unused access"
          value={unusedAccessEnabled
            ? `On - ${ageValid ? ageNumber : AGE_DEFAULT}-day window, ${homeRegion} only (billed)`
            : 'Off'}
        />
        <Alert severity="info" sx={{ mt: 2 }}>
          {memberAccountIds.length} account{memberAccountIds.length === 1 ? '' : 's'} ×{' '}
          {linkingRegions.length} region{linkingRegions.length === 1 ? '' : 's'} get a free
          external-access analyzer
          {unusedAccessEnabled
            ? `, plus one paid unused-access analyzer per account in ${homeRegion}.`
            : '.'}
          {' '}Where an analyzer of that type already exists, Prism adopts it instead of
          creating one - and never deletes an adopted analyzer, because that would
          destroy the findings it holds. Enrollment runs per account, so a failure in
          one does not stop the others.
        </Alert>
        {unusedAccessEnabled && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Unused access is billed at {UNUSED_ACCESS_PRICE}. {UNUSED_ACCESS_PRICE_NOTE}
          </Alert>
        )}
        <ZoneOfTrustNotice sx={{ mt: 2 }} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>
    );
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<VpnKeyOutlinedIcon sx={{ fontSize: 24 }} />}
          title="Set up IAM Access Analyzer"
          subtitle="Find external access and unused access across your organization"
          color={ACCESS_ANALYZER_COLOR}
        />
      </motion.div>
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={sectionCardSx}>
          {priorFailure && !config && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              The previous setup attempt failed: {priorFailure}. Submitting again will clear that state and retry.
            </Alert>
          )}
          <Stepper
            activeStep={active}
            alternativeLabel
            sx={{
              mb: 3,
              overflowX: 'auto',
              pb: 1,
              '& .MuiStepLabel-label': { fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.body },
              '& .MuiStepLabel-label.Mui-active': { fontWeight: 600, color: CK.navy },
              '& .MuiStepLabel-label.Mui-completed': { fontWeight: 600, color: CK.navy },
              '& .MuiStepIcon-root.Mui-active': { color: ACCESS_ANALYZER_COLOR },
              '& .MuiStepIcon-root.Mui-completed': { color: ACCESS_ANALYZER_COLOR },
            }}
          >
            {steps.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
          </Stepper>
          {body()}
          {!config && (
            <Box sx={{ ...actionRowSx, justifyContent: 'flex-end', mt: 3 }}>
              <Button disabled={active === ACCOUNTS_STEP} onClick={back} sx={quietButtonSx}>Back</Button>
              {active < REVIEW_STEP ? (
                <Button
                  variant="contained"
                  disabled={!stepComplete(active)}
                  onClick={next}
                  sx={accessAnalyzerButtonSx}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disabled={submitting || !canSubmit}
                  onClick={submit}
                  sx={accessAnalyzerButtonSx}
                >
                  {submitting ? 'Enabling…' : 'Enable Access Analyzer'}
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </motion.div>
    </motion.div>
  );
}
